import type { ColorMode } from "./colorModePreference";

export type ArkThemeTokens = Readonly<{
  canvas: string;
  sidebar: string;
  surface: string;
  surfaceElevated: string;
  border: string;
  divider: string;
  fill: string;
  fillSecondary: string;
  primary: string;
  primaryHover: string;
  primaryActive: string;
  primarySoft: string;
  primarySoftHover: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  textDisabled: string;
  textInverse: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  focusRing: string;
  focusShadow: string;
  headerSurface: string;
  navigationSurface: string;
  navigationSelectedSurface: string;
  navigationSelectedText: string;
  tableHeaderSurface: string;
  tableHeaderText: string;
  tableRowHover: string;
  fontFamily: string;
  fontSize: number;
  radius: number;
  radiusSmall: number;
  radiusLarge: number;
  radiusDialog: number;
  controlHeight: number;
  controlHeightSmall: number;
  controlHeightLarge: number;
  lineWidth: number;
}>;

const dimensions = {
  fontFamily:
    '"SF Pro Text", "PingFang SC", "Noto Sans CJK SC", "Microsoft YaHei", sans-serif',
  fontSize: 14,
  radius: 8,
  radiusSmall: 6,
  radiusLarge: 12,
  radiusDialog: 14,
  controlHeight: 36,
  controlHeightSmall: 28,
  controlHeightLarge: 40,
  lineWidth: 1,
} as const;

// Explicit semantic values are owned by Ark, never derived from a UI library.
const themes: Readonly<Record<ColorMode, ArkThemeTokens>> = Object.freeze({
  light: Object.freeze({
    ...dimensions,
    canvas: "#f3f5f8",
    sidebar: "#f5f7fa",
    surface: "#ffffff",
    surfaceElevated: "#ffffff",
    border: "#dfe3e9",
    divider: "#e8ebf0",
    fill: "#f5f7fa",
    fillSecondary: "rgba(0,0,0,0.06)",
    primary: "#146ef5",
    primaryHover: "#3d91ff",
    primaryActive: "#0650cf",
    primarySoft: "#e6f4ff",
    primarySoftHover: "#b8deff",
    text: "#1f2329",
    textSecondary: "#646a73",
    textTertiary: "#6a717b",
    textDisabled: "rgba(0,0,0,0.25)",
    textInverse: "#ffffff",
    success: "#52c41a",
    warning: "#faad14",
    danger: "#ff4d4f",
    info: "#1677ff",
    focusRing: "#146ef5",
    focusShadow: "0 0 0 2px rgba(20, 110, 245, 0.12)",
    headerSurface: "rgba(255, 255, 255, 0.9)",
    navigationSurface: "#f7f8fa",
    navigationSelectedSurface: "#eaf2ff",
    navigationSelectedText: "#1456b8",
    tableHeaderSurface: "#f7f8fa",
    tableHeaderText: "#646a73",
    tableRowHover: "#f5f8fc",
  }),
  dark: Object.freeze({
    ...dimensions,
    canvas: "#11151b",
    sidebar: "#20252d",
    surface: "#1b1f26",
    surfaceElevated: "#222730",
    border: "#343b47",
    divider: "#292f39",
    fill: "#20252d",
    fillSecondary: "rgba(255,255,255,0.12)",
    primary: "#4f91ff",
    primaryHover: "#6e9fe8",
    primaryActive: "#3a65ad",
    primarySoft: "#263a5b",
    primarySoftHover: "#2f4c7e",
    text: "#edf1f7",
    textSecondary: "#a7b0bf",
    textTertiary: "#7e8999",
    textDisabled: "rgba(255,255,255,0.25)",
    textInverse: "#ffffff",
    success: "#49aa19",
    warning: "#d89614",
    danger: "#dc4446",
    info: "#1668dc",
    focusRing: "#4f91ff",
    focusShadow: "0 0 0 2px rgba(79, 145, 255, 0.18)",
    headerSurface: "rgba(27, 31, 38, 0.92)",
    navigationSurface: "#161a21",
    navigationSelectedSurface: "#20304a",
    navigationSelectedText: "#75a7ff",
    tableHeaderSurface: "#20252d",
    tableHeaderText: "#b9c1ce",
    tableRowHover: "#222933",
  }),
});

export function createArkTokens(mode: ColorMode): ArkThemeTokens {
  return themes[mode];
}

export const arkThemeVariables = {
  canvas: "--ark-canvas",
  sidebar: "--ark-sidebar",
  surface: "--ark-surface",
  surfaceElevated: "--ark-surface-elevated",
  border: "--ark-border",
  divider: "--ark-divider",
  fill: "--ark-fill",
  fillSecondary: "--ark-fill-secondary",
  primary: "--ark-primary",
  primaryHover: "--ark-primary-hover",
  primaryActive: "--ark-primary-active",
  primarySoft: "--ark-primary-soft",
  primarySoftHover: "--ark-primary-soft-hover",
  text: "--ark-text",
  textSecondary: "--ark-text-secondary",
  textTertiary: "--ark-text-tertiary",
  textDisabled: "--ark-text-disabled",
  textInverse: "--ark-text-inverse",
  success: "--ark-success",
  warning: "--ark-warning",
  danger: "--ark-danger",
  info: "--ark-info",
  focusRing: "--ark-focus-ring",
  focusShadow: "--ark-focus-shadow",
  headerSurface: "--ark-header-surface",
  navigationSurface: "--ark-navigation-surface",
  navigationSelectedSurface: "--ark-navigation-selected-surface",
  navigationSelectedText: "--ark-navigation-selected-text",
  tableHeaderSurface: "--ark-table-header-surface",
  tableHeaderText: "--ark-table-header-text",
  tableRowHover: "--ark-table-row-hover",
  fontFamily: "--ark-font-family",
  fontSize: "--ark-font-size",
  radius: "--ark-radius",
  radiusSmall: "--ark-radius-small",
  radiusLarge: "--ark-radius-large",
  radiusDialog: "--ark-radius-dialog",
  controlHeight: "--ark-control-height",
  controlHeightSmall: "--ark-control-height-small",
  controlHeightLarge: "--ark-control-height-large",
  lineWidth: "--ark-line-width",
} as const satisfies Record<keyof ArkThemeTokens, `--ark-${string}`>;

export function createArkCssVariables(mode: ColorMode) {
  const tokens = createArkTokens(mode);
  return Object.fromEntries(
    (Object.keys(arkThemeVariables) as Array<keyof ArkThemeTokens>).map(
      (key) => [
        arkThemeVariables[key],
        typeof tokens[key] === "number" ? `${tokens[key]}px` : tokens[key],
      ],
    ),
  );
}

function declarations(mode: ColorMode) {
  return Object.entries(createArkCssVariables(mode))
    .map(([name, value]) => `${name}:${value};`)
    .join("");
}

export const arkThemeStyleText = `:root{${declarations("light")}}:root[data-color-mode="dark"]{${declarations("dark")}}`;
